import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader } from "@/components/StatusBadge";
import { PaymentProfileForm } from "@/components/payments/PaymentProfileForm";
import { StatementsTable } from "@/components/payments/StatementsTable";
import { StatementDialog } from "@/components/payments/StatementDialog";
import { ProfilesTable } from "@/components/payments/ProfilesTable";
import { ReportsPanel } from "@/components/payments/ReportsPanel";
import { money } from "@/components/payments/common";
import { t } from "@/i18n";

const Stat = ({ label, value, testId, accent }) => (
  <div className="panel p-4" data-testid={testId}><p className="eyebrow">{t(label)}</p><p className={`font-display text-2xl font-bold mt-1 tabular-nums ${accent || ""}`}>{value}</p></div>
);

const useStatements = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const load = () => Promise.all([api.get("/payments/statements"), api.get("/payments/summary")]).then(([a, b]) => { setItems(a.data); setSummary(b.data); });
  useEffect(() => { load(); }, []);
  return { items, summary, load };
};

const MyPayments = () => {
  const { items, summary, load } = useStatements();
  return (
    <div className="space-y-8" data-testid="my-payments">
      <PageHeader eyebrow="Finanzas" title="Pagos" description="Registra tus datos fiscales y bancarios para recibir tus regalías por transferencia, y consulta tus statements." />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Por pagar" value={money(summary.pending_net)} testId="my-pending" accent="text-amber-300" />
        <Stat label="Pagado" value={money(summary.paid_net)} testId="my-paid" accent="text-emerald-400" />
        <Stat label="Statements" value={summary.pending_count + summary.paid_count || 0} testId="my-count" />
      </div>
      <section className="space-y-3"><h2 className="font-display text-lg font-semibold">{t("Mis statements")}</h2><StatementsTable items={items} admin={false} /></section>
      <section className="space-y-3"><h2 className="font-display text-lg font-semibold">{t("Datos fiscales y de pago")}</h2><PaymentProfileForm onSaved={load} /></section>
    </div>
  );
};

const AdminPayments = () => {
  const { items, summary, load } = useStatements();
  const [profiles, setProfiles] = useState([]);
  const [open, setOpen] = useState(false);
  const loadProfiles = () => api.get("/payments/profiles").then((r) => setProfiles(r.data));
  useEffect(() => { loadProfiles(); }, []);
  const refresh = () => { load(); loadProfiles(); };

  const markPaid = async (s) => {
    const reference = window.prompt(t("Referencia de la transferencia (opcional):")) ?? "";
    try { await api.put(`/payments/statements/${s.id}`, { status: "paid", reference }); toast.success(t("Marcado como pagado")); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const unpay = async (s) => { try { await api.put(`/payments/statements/${s.id}`, { status: "pending" }); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (s) => { if (!window.confirm(t("¿Eliminar este statement?"))) return; try { await api.delete(`/payments/statements/${s.id}`); refresh(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="space-y-6" data-testid="admin-payments">
      <PageHeader eyebrow="Finanzas" title="Pagos y statements" description="Sube los reportes, registra lo que corresponde a cada artista y controla formularios fiscales y cuentas bancarias. Solo el administrador ve esta información.">
        <Button onClick={() => setOpen(true)} className="gap-2" data-testid="new-statement-btn"><Plus className="h-4 w-4" /> {t("Nuevo statement")}</Button>
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Stat label="Por pagar" value={money(summary.pending_net)} testId="sum-pending" accent="text-amber-300" />
        <Stat label="Pagado" value={money(summary.paid_net)} testId="sum-paid" accent="text-emerald-400" />
        <Stat label="Bruto total" value={money(summary.gross_total)} testId="sum-gross" />
        <Stat label="Retenido" value={money(summary.withholding_total)} testId="sum-withholding" />
        <Stat label="Formularios por revisar" value={summary.tax_forms_to_review ?? 0} testId="sum-review" accent={summary.tax_forms_to_review ? "text-sky-300" : ""} />
        <Stat label="Formularios faltantes" value={summary.tax_forms_missing ?? 0} testId="sum-missing" accent={summary.tax_forms_missing ? "text-amber-300" : ""} />
      </div>
      <Tabs defaultValue="statements">
        <TabsList data-testid="payments-tabs">
          <TabsTrigger value="statements" data-testid="tab-statements">{t("Statements")}</TabsTrigger>
          <TabsTrigger value="profiles" data-testid="tab-profiles">{t("Cuentas y formularios")}</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">{t("Reportes")}</TabsTrigger>
        </TabsList>
        <TabsContent value="statements" className="mt-4"><StatementsTable items={items} admin onMarkPaid={markPaid} onUnpay={unpay} onDelete={remove} /></TabsContent>
        <TabsContent value="profiles" className="mt-4"><ProfilesTable items={profiles} onChanged={refresh} /></TabsContent>
        <TabsContent value="reports" className="mt-4"><ReportsPanel onApplied={refresh} /></TabsContent>
      </Tabs>
      <StatementDialog open={open} onClose={() => setOpen(false)} onCreated={refresh} profiles={profiles} />
    </div>
  );
};

export default function PaymentsPage() {
  const { can } = useAuth();
  return can("admin") ? <AdminPayments /> : <MyPayments />;
}
